import { Link } from "react-router-dom";
import { ArrowRight, BadgeCheck, MessageSquareQuote } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Reveal, Chapter } from "@/components/Reveal";
import { EXPECTATIONS, FAQS, IMAGES, APPLY_PATH } from "@/data/content";

export const Expectations = () => (
  <section className="relative py-24 lg:py-32" data-testid="expectations-section">
    <div className="container-x">
      <Reveal>
        <Chapter index="09" label="Partner Expectations" />
        <div className="grid items-end gap-6 lg:grid-cols-[1.5fr_1fr]">
          <h2 className="font-display text-2xl font-bold tracking-tight text-offwhite sm:text-3xl lg:text-4xl">
            What Approved Partners <span className="text-aqua">Can Expect</span>
          </h2>
          <p className="text-sm leading-relaxed text-muted lg:text-right">
            A structured program with documented standards — not promises of results we cannot verify.
          </p>
        </div>
      </Reveal>

      <div className="mt-12 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {EXPECTATIONS.map((e, i) => (
          <Reveal key={e} delay={i * 0.05}>
            <div className="card-line flex items-center gap-3.5 rounded-xl p-5" data-testid={`expectation-${i}`}>
              <BadgeCheck size={18} className="shrink-0 text-aqua" />
              <span className="text-sm font-semibold text-offwhite/90">{e}</span>
            </div>
          </Reveal>
        ))}
        <Reveal delay={0.35}>
          <div className="flex items-center gap-3.5 rounded-xl border border-dashed border-aqua/25 bg-aqua/[0.04] p-5" data-testid="testimonial-placeholder">
            <MessageSquareQuote size={18} className="shrink-0 text-aqua/70" />
            <span className="text-sm leading-snug text-muted">
              Verified partner testimonials will be published here as approved partners share their results.
            </span>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

export const FAQ = () => (
  <section id="faq" className="relative bg-charcoal/30 py-24 lg:py-32" data-testid="faq-section">
    <div className="container-x grid items-start gap-12 lg:grid-cols-[1fr_1.6fr]">
      <Reveal>
        <Chapter index="10" label="FAQ" />
        <h2 className="font-display text-2xl font-bold tracking-tight text-offwhite sm:text-3xl lg:text-4xl">
          Frequently Asked <span className="text-aqua">Questions</span>
        </h2>
        <p className="mt-4 max-w-md text-base leading-relaxed text-muted">
          Straight answers about the program, the revenue model and the review process. Applying never guarantees
          approval or deployment.
        </p>
        <Link to={APPLY_PATH} className="btn-primary mt-8" data-testid="faq-apply-btn">
          Apply Now <ArrowRight size={16} />
        </Link>
      </Reveal>

      <Reveal delay={0.1}>
        <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
          {FAQS.map((f, i) => (
            <AccordionItem
              key={f.q}
              value={`faq-${i}`}
              className="card-line overflow-hidden rounded-xl border px-6 data-[state=open]:border-aqua/30"
              data-testid={`faq-item-${i}`}
            >
              <AccordionTrigger className="py-5 text-left font-display text-base font-semibold text-offwhite hover:text-aqua hover:no-underline [&[data-state=open]]:text-aqua">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="pb-5 text-sm leading-relaxed text-muted">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </div>
  </section>
);

export const FinalCTA = () => (
  <section className="noise relative overflow-hidden py-28 lg:py-40" data-testid="final-cta-section">
    <div className="absolute inset-0">
      <img
        src={IMAGES.marinaNight}
        alt="Waterfront marina at night — the stage for the next premium watersports experience"
        className="h-full w-full object-cover"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-ink via-ink/80 to-ink/95" />
    </div>
    <div className="container-x relative z-10 text-center">
      <Reveal>
        <p className="eyebrow mb-6">Final Call</p>
        <h2 className="mx-auto max-w-4xl font-display text-3xl font-extrabold uppercase leading-[1.05] tracking-tight text-offwhite sm:text-4xl lg:text-5xl" data-testid="final-cta-headline">
          Bring the Next Premium Watersports Experience <span className="text-aqua">to Your Market</span>
        </h2>
        <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-muted sm:text-lg">
          If your company operates from a qualified waterfront location and is ready to introduce a high-visibility
          premium attraction, submit your Fleet Partner application for review.
        </p>
        <div className="mt-10 flex justify-center">
          <Link to={APPLY_PATH} className="btn-primary animate-pulse-ring" data-testid="final-cta-apply-btn">
            Apply to Become a Fleet Partner <ArrowRight size={16} />
          </Link>
        </div>
      </Reveal>
    </div>
  </section>
);
